import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { getUserOrganizations } from '../../api/organizationApi';
import type { Organization } from '../../types/organization';

export function OrgSwitcherSheet() {
  const user = useAuthStore((s) => s.user);
  const selectedOrganizationId = useAuthStore((s) => s.selectedOrganizationId);
  const switchToOrganization = useAuthStore((s) => s.switchToOrganization);

  const pushScreen = useTabNavigationStore((s) => s.pushScreen);
  const closeOrgSwitcher = useTabNavigationStore((s) => s.closeOrgSwitcher);

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    const loadOrgs = async () => {
      try {
        setLoading(true);
        const orgs = await getUserOrganizations();
        setOrganizations(orgs);
      } catch (err) {
        console.error('Failed to load organizations:', err);
      } finally {
        setLoading(false);
      }
    };

    loadOrgs();
  }, [user?.id]);

  const handleSelect = async (orgId: string) => {
    try {
      await switchToOrganization(orgId);
      closeOrgSwitcher();
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch organization:', err);
    }
  };

  const handleCreate = () => {
    closeOrgSwitcher();
    pushScreen('create-organization');
  };

  const handleJoin = () => {
    closeOrgSwitcher();
    pushScreen('join-organization');
  };

  const getRoleLabel = (org: Organization): string => {
    // Use owner_id to determine role
    if (org.owner_id === user?.id) return 'Владелец';
    return 'Админ';
  };

  return (
    <div className="py-4">
      <h2
        className="text-xl leading-6 font-semibold mb-4"
        style={{ color: 'var(--text-primary)' }}
      >
        Организации
      </h2>

      {loading ? (
        <div className="py-8 text-center">
          <p className="text-[15px]" style={{ color: 'var(--text-hint)' }}>
            Загрузка...
          </p>
        </div>
      ) : organizations.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-[15px] mb-2" style={{ color: 'var(--text-hint)' }}>
            У вас пока нет организаций
          </p>
          <p className="text-[13px]" style={{ color: 'var(--text-hint)' }}>
            Создайте свою или присоединитесь по приглашению
          </p>
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          {organizations.map((org) => {
            const isActive = org.id === selectedOrganizationId;
            return (
              <button
                key={org.id}
                className="w-full rounded-xl p-4 flex items-center justify-between active:opacity-60 transition-opacity"
                style={{ backgroundColor: 'var(--bg-secondary)' }}
                onClick={() => handleSelect(org.id)}
              >
                <div className="text-left">
                  <p
                    className="text-[17px] leading-[22px] font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {org.name}
                  </p>
                  <p
                    className="text-[15px] leading-5"
                    style={{ color: 'var(--text-hint)' }}
                  >
                    {getRoleLabel(org)}
                    {org.subscription_tier && (
                      <span className="ml-2 opacity-60">
                        {org.subscription_tier.toUpperCase()}
                      </span>
                    )}
                  </p>
                </div>
                {isActive && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--accent)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="secondary" onClick={handleCreate} fullWidth>
          Создать
        </Button>
        <Button variant="secondary" onClick={handleJoin} fullWidth>
          Присоединиться
        </Button>
      </div>
    </div>
  );
}
